// ============================================================
// ScorePanel.tsx — 姿勢評分顯示面板
// 顯示總分、評級、及各部位分項分數
// ============================================================

import React, { FC } from 'react';
import { PostureAnalysisResult, PostureScoreBreakdown } from 'types/posture';
import { GRADE_COLORS } from 'utils/poseConstants';

interface ScorePanelProps {
    analysis: PostureAnalysisResult;
}

// ── 圓形進度條（SVG 實現） ─────────────────────────────────
interface CircleGaugeProps {
    score: number;
    grade: string;
    size?: number;
}

const CircleGauge: FC<CircleGaugeProps> = ({ score, grade, size = 120 }) => {
    const radius      = (size - 20) / 2;
    const circumference = 2 * Math.PI * radius;
    const progress    = ((100 - score) / 100) * circumference; // 反向計算已填充長度

    // 根據分數決定顏色
    const color =
        score >= 90 ? '#FFD700' :   // 金色
            score >= 80 ? '#00FF88' :   // 綠色
                score >= 70 ? '#00AAFF' :   // 藍色
                    score >= 60 ? '#FF8C00' :   // 橙色
                        '#FF4444';                   // 紅色

    return (
        <div style={{ position: 'relative', width: size, height: size }}>
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                {/* 背景圓環 */}
                <circle
                    cx={size / 2} cy={size / 2} r={radius}
                    fill="none" stroke="#2a2a3e" strokeWidth={8}
                />
                {/* 分數圓環 */}
                <circle
                    cx={size / 2} cy={size / 2} r={radius}
                    fill="none" stroke={color} strokeWidth={8}
                    strokeDasharray={circumference}
                    strokeDashoffset={progress}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                />
            </svg>
            {/* 分數文字（疊加在圓環中央） */}
            <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
            }}>
                <span style={{ fontSize: 28, fontWeight: 700, color }}>{score}</span>
                <span className={`grade-badge ${GRADE_COLORS[grade]}`}
                      style={{ fontSize: 14, fontWeight: 600 }}>
          {grade}
        </span>
            </div>
        </div>
    );
};

// ── 分項分數條 ────────────────────────────────────────────
interface ScoreBarProps {
    label: string;
    score: number;
}

const ScoreBar: FC<ScoreBarProps> = ({ label, score }) => {
    const color =
        score >= 90 ? '#00FF88' :
            score >= 70 ? '#00AAFF' :
                score >= 50 ? '#FF8C00' : '#FF4444';

    return (
        <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#aaa' }}>{label}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color }}>{score}</span>
            </div>
            <div style={{ background: '#2a2a3e', borderRadius: 4, height: 6 }}>
                <div style={{
                    width: `${score}%`, height: '100%', borderRadius: 4,
                    background: color, transition: 'width 0.5s ease',
                }} />
            </div>
        </div>
    );
};

// ── 主元件 ───────────────────────────────────────────────
const ScorePanel: FC<ScorePanelProps> = ({ analysis }) => {
    const { totalScore, grade, scores } = analysis;

    const scoreItems: { label: string; key: keyof PostureScoreBreakdown }[] = [
        { label: '頭部 / 頸部',  key: 'head' },
        { label: '肩膀水平',     key: 'shoulders' },
        { label: '脊椎垂直',     key: 'spine' },
        { label: '髖骨水平',     key: 'hips' },
    ];

    return (
        <div className="score-panel">
            <h3 className="panel-title">即時姿勢評分</h3>

            {/* 總分圓形儀表 */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <CircleGauge score={totalScore} grade={grade} />
            </div>

            {/* 分項分數 */}
            <div className="score-breakdown">
                {scoreItems.map(({ label, key }) => (
                    <ScoreBar key={key} label={label} score={scores[key]} />
                ))}
            </div>
        </div>
    );
};

export default ScorePanel;
